<script lang="ts" setup>
const props = withDefaults(defineProps<{
  embedCode: string
  persist?: boolean
}>(), {
  persist: false,
})

const emit = defineEmits<{
  (e: 'load' | 'error'): void
}>()

const generateHash = (string: string) => {
  let hash = 0
  for (const char of string) {
    hash = (hash << 5) - hash + char.charCodeAt(0)
    hash |= 0 // Constrain to 32bit integer
  }
  return hash
}

const instanceId = generateHash(props.embedCode)
const savedEmbedIds = useState<number[]>('savedEmbedIds', () => [])
const hasSavedElement = computed(() => savedEmbedIds.value.includes(instanceId))

// Resolves with `true` on success, `false` on failure.
type Deferred = { promise: Promise<boolean>, resolve: (ok: boolean) => void }

const createDeferred = (): Deferred => {
  let resolve!: (ok: boolean) => void
  const promise = new Promise<boolean>((res) => {
    resolve = res
  })
  return { promise, resolve }
}

const iframeDeferreds = new Map<string, Deferred>()
const scriptDeferreds = new Map<string, Deferred>()
let loadEmitted = false
let errorEmitted = false

const getIframeDeferred = (key: string): Deferred => {
  let deferred = iframeDeferreds.get(key)
  if (!deferred) {
    deferred = createDeferred()
    iframeDeferreds.set(key, deferred)
  }
  return deferred
}

const onIframeLoad = (key: string) => {
  getIframeDeferred(key).resolve(true)
}

const onIframeError = (key: string) => {
  getIframeDeferred(key).resolve(false)
}

const emitWhenLoaded = async () => {
  if (loadEmitted) {
    return
  }

  const iframePromises = iframeDataList.value.map(
    iframeData => getIframeDeferred(iframeData.key).promise,
  )
  const scriptPromises = Array.from(scriptDeferreds.values()).map(
    deferred => deferred.promise,
  )

  const results = await Promise.all([...iframePromises, ...scriptPromises])

  if (results.includes(false) && !errorEmitted) {
    errorEmitted = true
    emit('error')
  }

  if (!loadEmitted) {
    loadEmitted = true
    emit('load')
  }
}

/*
  We expect the embed code contains <div>, <iframe>, <form> and <script> tags.
  The <div>, <iframe> and <form> tags can contain any attributes, which will be applied to the elements rendered by this component.
  The <form> tag's inner HTML (inputs, labels, buttons, etc.) is preserved and rendered via v-html.
  The <script> tag can contain any attributes, which will be applied to the script tags added to the body.
  Script tags can be external (with src attribute) or inline (with content).

  Tested with:
  - Publitas (multiple)
  - Fillout
  - Flockler
  - Youtube
  - Flowbox
  - OnlineAfspraken.nl
  - YouShouldAsk.ai
  - Mailcampaigns

  Works possibly with:
  - Vimeo
  - TikTok

  ToDo:
  - Style tags

  Exmples:

  --- Publitas ---

  <div id="publitas-embed-ixmwaih1e9"></div>
  < script  data-cfasync="false"  data-height="undefined"  data-publication="https://view.publitas.com/woonboulevard-poortvliet/folder-keukendepot-september/"  data-publication-aspect-ratio="1.4143007360672977"  data-publication-title="Folder | September | Keukendepot"  data-responsive="true"  data-width="undefined"  data-wrapper-id="publitas-embed-ixmwaih1e9"  publitas-embed  src="https://view.publitas.com/embed.js"  type="text/javascript">< /script>

  --- Flowbox ---

  < script>
    (function(d, id) {
      if (!window.flowbox) { var f = function () { f.q.push(arguments); }; f.q = []; window.flowbox = f; }
      if (d.getElementById(id)) {return;}
      var s = d.createElement('script'), fjs = d.scripts[d.scripts.length - 1]; s.id = id; s.async = true;
      s.src = 'https://connect.getflowbox.com/flowbox.js';
      fjs.parentNode.insertBefore(s, fjs);
    })(document, 'flowbox-js-embed');
  < /script>

  <div id="js-flowbox-flow"></div>

  < script>
    window.flowbox('init', {
      container: '#js-flowbox-flow',
      key: 'xLP-d-iJRSeePxZIYA4GKQ',
      locale: 'en-GB',
    })
  < /script>

  --- Fillout ---

  <div style="width:100%;height:500px;" data-fillout-id="ndQbqWeu1Jus" data-fillout-embed-type="standard" data-fillout-inherit-parameters data-fillout-dynamic-resize></div>
  < script src="https://server.fillout.com/embed/v1/">< /script>

  --- Online Afpraken ---

  <div id="oa_widget" class="widget-js"></div>
  < script type="text/javascript" src="https://imgwidget.onlineafspraken.nl/consumer/booking/book/key/lhba46bgva93-zzba02/l/11121/ln/nl/t/808084/f/e0c010e/o/theme:advanced,dp:modern/c/00aeef,a36209,262626,a36209,959595,fff,0,fff/at/292646,309493,314374/rs/0/pp/0/exclude/mobiledetect/output/js"></ script>

  --- You Should Ask ---

  < script type="text/javascript" async src="https://app.youshouldask.ai/chat/js/E8Br0z/lzBaQy">< /script>

  --- Mailcampaigns ---

  <form accept-charset="utf-8" action="//interface.mailcampaigns.nl/interface/contactform.php?ref=p3IvoJy0Mz9loI8lZmVjKmD3ZQR=" method="post" id="sub_mc_form" enctype="multipart/form-data" name=
  "sub_mc_form">
    <input type="hidden" name="ipaddress" value="87.233.96.184">
    <p><div class="form-table">
      <div class="form-row">
        <div class="form-cell form-label">
          <div class="form-label">
            Voornaam
          </div>
        </div>
        <div class="form-cell">
          <div class="std-tbx">
            <input value="" placeholder="Voornaam" type="text" name="field_names[MTAzNDEz][]" id="MTAzNDEz">
          </div>
        </div>
      </div></p>
      <p><div class="form-row">
        <div class="form-cell form-label">
          <div class="form-label">
            Achternaam
          </div>
        </div>
        <div class="form-cell">
          <div class="std-tbx">
            <input value="" placeholder="Achternaam" type="text" name="field_names[MTAzNDE2][]" id="MTAzNDE2">
          </div>
        </div>
      </div></p>
    <p> <div class="form-row">
        <div class="form-cell form-label">
          <div class="form-label">
            E-mail <span class=\'required_icon\'>*</span>
          </div>
        </div>
        <div class="form-cell">
          <div class="std-tbx">
            <input value="" placeholder="E-mail" type="text" name="field_names[MTAyNzAz][]" id="MTAyNzAz" data-validation="email">
          </div>
        </div>
      </div>
    </div></p>
    <div class="submitButtonClass">
      <button type="submit" class="submit">Schrijf je in</button>
      <div id="mc_feedback_message" class="mc_feedback_message"></div>
    </div>
  </form>
  <br>
*/

const iframeDataList = computed(() => {
  const iframeMatches = Array.from(props.embedCode.matchAll(/<iframe([^>]*)\/?>(?:<\/iframe>)?/g))
  return iframeMatches.map((match, index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    return { ...attributes, key: `iframe__${instanceId}__${index}` }
  })
})

const divDataList = computed(() => {
  const divMatches = Array.from(props.embedCode.matchAll(/<div([^>]*)>(?:.*?<\/div>)?/gs))
  return divMatches.map((match, index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    return { ...attributes, key: `div__${instanceId}__${index}` }
  })
})

const formDataList = computed(() => {
  const formMatches = Array.from(props.embedCode.matchAll(/<form([^>]*)>(.*?)<\/form>/gs))
  return formMatches.map((match, index) => {
    const attrs = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    return { attrs, innerHTML: match[2]?.trim() || '', key: `form__${instanceId}__${index}` }
  })
})

const scriptDataList = computed(() => {
  const scriptMatches = Array.from(props.embedCode.matchAll(/<script([^>]*)>(.*?)<\/script>/gs))

  return scriptMatches.map((match, _index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    const scriptContent = match[2]?.trim()
    return {
      ...attributes,
      content: scriptContent || undefined,
      origin: match[0],
    }
  })
})

const scriptConfigs = computed(() => {
  return scriptDataList.value.map((scriptData, index) => {
    const id = `script__${instanceId}__${index}`
    const key = scriptData.origin || id

    return {
      key,
      id: scriptData.id || id,
      defer: scriptData.src ? true : undefined,
      ...scriptData,
    }
  })
})

const { initiatedThirdParty } = useApp()

function initScripts() {
  if (!initiatedThirdParty.value) {
    return
  }

  scriptConfigs.value.forEach((scriptConfig) => {
    if (!document.getElementById(scriptConfig.id)) {
      const script = document.createElement('script')

      Object.entries(scriptConfig).forEach(([key, value]) => {
        if (key === 'key' || key === 'content') {
          return
        }

        if (value === true) {
          script.setAttribute(key, '')
        }
        else if (value !== undefined) {
          script.setAttribute(key, value as string)
        }
      })

      if (scriptConfig.content) {
        script.textContent = scriptConfig.content
      }

      // Only external scripts (with src) fire load/error events.
      // Inline scripts execute synchronously on append, so they need no tracking.
      if (scriptConfig.src) {
        const deferred = createDeferred()
        scriptDeferreds.set(scriptConfig.id, deferred)
        script.addEventListener('load', () => deferred.resolve(true))
        script.addEventListener('error', () => deferred.resolve(false))
      }

      document.body.appendChild(script)
    }
  })
}

const placeholder = useTemplateRef('placeholder')

const save = () => {
  if (!props.persist) {
    return
  }

  const saveSpot = document.getElementById('teleports')
  // Use our own placeholder ref to find this instance's element,
  // avoiding getElementById which can grab the wrong element when
  // two instances with the same embedCode coexist during route transitions.
  const contentToMove = placeholder.value?.firstElementChild as HTMLElement

  if (saveSpot && contentToMove) {
    if ('moveBefore' in document) {
      saveSpot.moveBefore(contentToMove, null)
    }
    else {
      saveSpot.insertBefore(contentToMove, null)
    }
    if (!savedEmbedIds.value.includes(instanceId)) {
      savedEmbedIds.value.push(instanceId)
    }
  }
}

const restore = () => {
  if (!props.persist || !placeholder.value) {
    return
  }

  const contentToMove = document.getElementById('elm__' + instanceId)
  if (contentToMove) {
    if ('moveBefore' in placeholder.value) {
      placeholder.value.moveBefore(contentToMove, null)
    }
    else {
      placeholder.value.insertBefore(contentToMove, null)
    }
  }
}

watch(initiatedThirdParty, (value) => {
  if (!value) return

  // Use nextTick to wait for the v-if placeholder to render
  nextTick(() => {
    if (hasSavedElement.value) {
      restore()
      // Already loaded previously; signal immediately.
      if (!loadEmitted) {
        loadEmitted = true
        emit('load')
      }
    }
    else {
      initScripts()
      emitWhenLoaded()
    }
  })
})

onMounted(() => {
  if (!initiatedThirdParty.value) return

  if (hasSavedElement.value) {
    // Defer restore to next tick so the old instance's onBeforeUnmount
    // has time to save its element first (simultaneous route transitions)
    nextTick(() => {
      restore()
      // Already loaded previously; signal immediately.
      if (!loadEmitted) {
        loadEmitted = true
        emit('load')
      }
    })
  }
  else {
    initScripts()
    emitWhenLoaded()
  }
})

onBeforeUnmount(() => {
  save()
})
</script>

<template>
  <div
    v-if="initiatedThirdParty"
    ref="placeholder"
    class="ui-embed"
  >
    <div
      v-if="!hasSavedElement"
      :id="`elm__${instanceId}`"
    >
      <div
        v-for="divData in divDataList"
        :key="divData.key"
        v-bind="divData"
      />
      <iframe
        v-for="iframeData in iframeDataList"
        :key="iframeData.key"
        v-bind="iframeData"
        @load="onIframeLoad(iframeData.key)"
        @error="onIframeError(iframeData.key)"
      />
      <form
        v-for="formData in formDataList"
        :key="formData.key"
        v-bind="formData.attrs"
        v-html="formData.innerHTML"
      />
    </div>
  </div>
</template>

<style>
@import url('~layers/components/Ui/Embed.css');
</style>
